import { withYoutubeCaptions, youtubeCaptionLang } from '~/utils/youtubeCaptions';
import { applyCaptionTranslation, loadYouTubeApi, type YTPlayerInstance } from '~/composables/useYoutubePlayer';

// For plain <iframe> embeds: caption params for the src, plus auto-translated captions once the video plays.
export const useYoutubeCaptions = (iframe: Ref<HTMLIFrameElement | null>, src?: () => string | null | undefined) => {
  const { locale } = useI18n();
  let player: YTPlayerInstance | null = null;
  let stopCaptions: (() => void) | null = null;
  let attachId = 0;

  const detach = () => {
    stopCaptions?.();
    stopCaptions = null;
    player = null;
  };

  const start = () => {
    const lang = youtubeCaptionLang(locale.value);
    if (lang && player && !stopCaptions) stopCaptions = applyCaptionTranslation(player, lang);
  };

  const attach = async (el: HTMLIFrameElement | null) => {
    detach();
    const id = ++attachId;
    if (!el || !youtubeCaptionLang(locale.value)) return;
    await loadYouTubeApi();
    if (!window.YT?.Player || id !== attachId || iframe.value !== el) return;
    player = new window.YT.Player(el, {
      events: {
        onReady: ({ target }) => {
          if (target.getPlayerState?.() === 1) start();
        },
        onStateChange: ({ data }) => {
          if (data === 1) start();
        },
      },
    });
  };

  // Re-attach when the element or its src changes (a reloaded iframe drops the API handshake).
  // Started on mount so callers can declare `src` after this call.
  let stopWatch: (() => void) | null = null;
  onMounted(() => {
    attach(iframe.value);
    stopWatch = watch([iframe, () => src?.()], ([el]) => attach(el), { flush: 'post' });
  });
  onBeforeUnmount(() => {
    stopWatch?.();
    attachId += 1;
    detach();
  });

  return { embedSrc: (url: string) => withYoutubeCaptions(url, locale.value) };
};
